/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import type { TrackingData } from 'js/lib/retracked';

import { Chip, Grid, Skeleton, TabContext, Typography2, breakpoints, typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import type { Pathway } from 'bundles/page-config-common/components/shared/complex-spotlight-collection/toPathway';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import useIsTablet from 'bundles/unified-common/hooks/useIsTablet';

import _t from 'i18n!nls/consumer-home';

// Extract props type from LearningProductCard and exclude props that are explicitly provided by this component
type LearningProductCardProps = React.ComponentPropsWithoutRef<typeof LearningProductCard>;
type ComponentPropsReturn = Partial<Omit<LearningProductCardProps, 'id' | 'name' | 'cardIndex' | 'titleComponent'>>;

const CARD_DESKTOP_WIDTH = 400;
const CARD_MOBILE_WIDTH = 309;
const PLACEHOLDER_CARD_HEIGHT = 490;
const CARDS_PER_VIEWPORT = {
  MOBILE: 2, // Displays 2 cards max for mobile (1 full, 1 preview)
  TABLET: 3, // 2 full cards and 1 preview for 600px < width < 1024px widths
  DESKTOP: 4, // 3 full cards and 1 preview for width > 1024px
} as const;

const styles = {
  filtersCarousel: css`
    display: flex;
    gap: var(--cds-spacing-50);
    margin: var(--cds-spacing-200) 0;
    z-index: 1;

    .cds-Chip-root {
      white-space: nowrap;
    }

    .cds-Chip-selected {
      ${typography2.actionSecondary}
      background: var(--cds-color-neutral-primary-weak) !important;
    }
  `,
  cardsCarousel: (hasFilters: boolean, isSingleCard: boolean) => css`
    display: flex;
    gap: var(--cds-spacing-200);
    margin-top: ${hasFilters ? 'var(--cds-spacing-50)' : 'var(--cds-spacing-200)'};

    .cds-ProductCard-base {
      width: ${CARD_DESKTOP_WIDTH}px !important;
      height: 100% !important;

      ${breakpoints.down('sm')} {
        width: ${CARD_MOBILE_WIDTH}px !important;
      }

      .cds-ProductCard-statusTags {
        span {
          ${typography2.subtitleMedium};
          border-radius: var(--cds-border-radius-50);
        }
      }

      .cds-ProductCard-content {
        gap: var(--cds-spacing-100);

        h3.cds-CommonCard-title {
          ${typography2.subtitleLarge};

          ${breakpoints.down('sm')} {
            ${typography2.subtitleMedium};
          }
        }

        .cds-CommonCard-bodyContent {
          strong {
            ${typography2.actionSecondary};
          }

          p {
            line-clamp: 2;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 2;
            overflow: hidden;
          }
        }

        .cds-RatingStat-meter > span {
          ${typography2.subtitleMedium};
        }

        .cds-CommonCard-metadata > p {
          ${typography2.bodyTertiary};
        }
      }
    }
    ${isSingleCard &&
    css`
      .cds-ProductCard-base {
        width: 90% !important;

        ${breakpoints.down('md')} {
          width: 100% !important;

          .cds-ProductCard-listCard {
            .cds-ProductCard-statusTags {
              position: relative;
              top: 0;
              padding-left: 0;
            }
          }
        }
      }
    `}
  `,
  container: css`
    margin-bottom: var(--cds-spacing-300);

    a[data-testid='edit-goal-link'] {
      ${typography2.subtitleSmall};
      padding-bottom: calc(var(--cds-spacing-50) / 2);
    }
  `,
  cardLoadingComponentContainer: (index: number) => css`
    margin-bottom: var(--cds-spacing-300);
    width: ${CARD_DESKTOP_WIDTH}px;
    height: ${PLACEHOLDER_CARD_HEIGHT}px;
    flex-shrink: 0;

    ${breakpoints.down('sm')} {
      width: ${CARD_MOBILE_WIDTH}px;
    }

    // Ensure ProductCard skeleton respects container width
    .cds-ProductCard-base {
      width: 100% !important;
      max-width: ${CARD_DESKTOP_WIDTH}px !important;
    }

    .cds-ProductCard-gridSkeletonHeader {
      span {
        height: 24px !important;
      }

      /* Partners */
      span:first-of-type {
        width: 50% !important;
      }

      /* Title */
      span:last-of-type {
        width: 80% !important;
      }
    }

    .cds-ProductCard-gridSkeletonBody {
      gap: var(--cds-spacing-100) !important;
      margin-top: calc(var(--cds-spacing-100) * -1) !important;

      /* Skills you'll gain */
      span:first-of-type {
        height: 40px !important;
        width: 95% !important;
      }

      /* Rating */
      span:last-of-type {
        height: 25px !important;
        width: 30% !important;
      }
    }

    .cds-ProductCard-gridSkeletonFooter {
      margin-top: var(--cds-spacing-1000);

      /* Metadata */
      span:first-of-type {
        margin-top: calc(var(--cds-spacing-1000) * -1.48);
        height: 50px !important;
        width: 80% !important;
      }

      /* Enroll button */
      span:nth-of-type(2) {
        margin-top: calc(var(--cds-spacing-300) * -1.2);
        height: 60px !important;
        width: 30% !important;
      }

      /* Pathways recommendation coach */
      span:last-of-type {
        ${index === 0 &&
        css`
          margin-top: calc(var(--cds-spacing-300) * -1);
          height: 30px !important;
          width: 50% !important;
        `}
        ${index !== 0 &&
        css`
          display: none;
        `}
      }

      ${breakpoints.down('sm')} {
        span:nth-of-type(2),
        span:last-of-type {
          margin-top: calc(var(--cds-spacing-300));
        }
      }
    }
  `,
  loadingContainer: css`
    display: flex;
    gap: var(--cds-spacing-200);
  `,
  navigationButtonsLoadingState: css`
    margin-top: calc(var(--cds-spacing-400) * -1);
  `,
  noResultsFound: css`
    margin-top: var(--cds-spacing-800);
    margin-bottom: var(--cds-spacing-400);
    position: relative;
    display: flex;
    flex-direction: column;
    text-align: center;
    box-sizing: border-box;

    p {
      margin-top: var(--cds-spacing-100);
    }
  `,
};

type Collection<I> = {
  id: string;
  items: I[];
};

type Filter = {
  id: string;
  label: string;
};

type Props = {
  title: React.JSX.Element;
  collections: Array<Collection<Pathway['items'][0]>>;
  filters?: Filter[];
  onFilterSelect?: (id?: string) => void;
  selectedFilterId?: string;
  trackingData?: TrackingData;
  componentProps: (item: Pathway['items'][0], itemIndex: number) => ComponentPropsReturn;
  roleDetailsCta?: React.ReactNode;
  cardLoadingComponent?: React.ReactNode;
};

/**
 * @description Displays a carousel of LearningProductCards for Pathways collections with filtering capabilities.
 * @param filters - Used to filter collections displayed in the carousel
 * @param collections - Array of pathway collections
 * @param title - Section title element
 * @param onFilterSelect - Gets called when a filter is selected
 * @param selectedFilterId - ID of the selected filter
 * @param componentProps - Props to pass to the LearningProductCard component
 * @param roleDetailsCta - Optional CTA that displays after carousel navigation
 * @param trackingData - For eventing purposes
 * @param cardLoadingComponent - Displayed in place of a card when the carousel is loading
 */
const PathwaysCarouselCollections = ({
  filters,
  collections,
  title,
  onFilterSelect,
  selectedFilterId,
  componentProps,
  roleDetailsCta,
  trackingData,
  cardLoadingComponent,
}: Props) => {
  const trackV3 = useTracker();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const collectionsCarouselRef = React.useRef<HTMLDivElement>(null);

  // Simplified loading state management - track the filter ID we're waiting for
  const [pendingFilterId, setPendingFilterId] = React.useState<string | null>(null);

  const getMaxCardsForViewport = React.useCallback<() => number>(() => {
    if (isMobile) return CARDS_PER_VIEWPORT.MOBILE;
    if (isTablet) return CARDS_PER_VIEWPORT.TABLET;
    return CARDS_PER_VIEWPORT.DESKTOP;
  }, [isMobile, isTablet]);

  const scrollCarouselToStart = () => {
    const container = collectionsCarouselRef.current?.querySelector('[data-testid="overflow-carousel"]') as HTMLElement;
    const scrollableElement = container?.firstElementChild as HTMLElement;

    if (scrollableElement?.scrollTo) {
      scrollableElement.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (scrollableElement) {
      // Fallback for environments without scrollTo
      scrollableElement.scrollLeft = 0;
    }
  };

  // Reset scroll position when filter selection is completed
  React.useEffect(() => {
    if (pendingFilterId === selectedFilterId) {
      scrollCarouselToStart();
      setPendingFilterId(null); // Clear pending state once filter change is applied
    }
  }, [selectedFilterId, pendingFilterId]);

  const handleFilterSelect = (id?: string) => {
    setPendingFilterId(id ?? null);

    trackV3('click_button', {
      button: { name: 'collections_filter_chip', pageConfigComponent: trackingData?.pageConfigComponent },
      carousel: {
        ...trackingData?.carousel,
        id: String(trackingData?.carousel?.id || ''),
        name: filters?.find((f) => f.id === id)?.label,
      },
    });

    onFilterSelect?.(id);
  };

  // Determine current state and items to display
  const isLoading = Boolean(pendingFilterId && pendingFilterId !== selectedFilterId);
  const currentSelectedFilterId = isLoading ? pendingFilterId : selectedFilterId;
  const resolvedSelectedId = currentSelectedFilterId ?? collections[0]?.id;
  const selectedCollection = collections.find((c) => c.id === resolvedSelectedId) ?? collections[0];

  // Use placeholders during loading to minimize layout shift, otherwise use selected collection items
  const itemsToShow = React.useMemo(() => {
    if (isLoading && cardLoadingComponent) {
      return Array.from(
        { length: getMaxCardsForViewport() },
        (_, index) => ({ id: `LOADING_ITEM_ID_${index}` } as Pathway['items'][0])
      );
    }
    return selectedCollection?.items ?? [];
  }, [isLoading, cardLoadingComponent, getMaxCardsForViewport, selectedCollection?.items]);

  const renderCarouselItems = () => {
    if (isLoading && cardLoadingComponent) {
      return (
        <div css={styles.loadingContainer}>
          {itemsToShow.map((placeholderItem, index) => (
            <div key={`loading-placeholder-${placeholderItem.id}`} css={styles.cardLoadingComponentContainer(index)}>
              {cardLoadingComponent}
            </div>
          ))}
        </div>
      );
    }

    return itemsToShow.map((item, cardPosition) => (
      <LearningProductCard
        key={`${item.id}-${currentSelectedFilterId}`}
        {...componentProps(item, cardPosition)}
        id={item.id}
        variant={itemsToShow.length === 1 ? 'list' : 'grid'}
        name={item.name}
        cardIndex={cardPosition}
        titleComponent="h3"
        trackingProps={{
          additionalV3TrackingData: {
            productCard: {
              index: cardPosition,
              ...trackingData?.productCard,
            },
            ...(trackingData?.carousel?.id ? { carousel: { ...trackingData.carousel } } : {}),
          },
        }}
      />
    ));
  };

  return (
    <Grid item xs={12} css={styles.container}>
      {title}

      {filters && (
        <TabContext value={`pathways-carousel-collections-filters-${resolvedSelectedId}`}>
          <OverflowCarousel css={styles.filtersCarousel}>
            {filters.map((filter) => (
              <Chip
                key={filter.id}
                role="tab"
                aria-label={_t('Filter by #{filterName}', { filterName: filter.label })}
                selected={selectedFilterId === filter.id}
                onClick={() => handleFilterSelect(filter.id)}
              >
                {filter.label}
              </Chip>
            ))}
          </OverflowCarousel>
        </TabContext>
      )}

      <div ref={collectionsCarouselRef}>
        {itemsToShow.length === 0 && !isLoading && (
          <div css={styles.noResultsFound}>
            <Typography2 variant="subtitleLarge" component="h3">
              {_t('No results found for "#{filterName}"', {
                filterName: filters?.find((f) => f.id === selectedFilterId)?.label ?? '',
              })}
            </Typography2>
            <Typography2 variant="bodyPrimary" component="p">
              {_t('Try changing your filters')}
            </Typography2>
          </div>
        )}
        {itemsToShow.length > 1 ? (
          <OverflowCarousel
            showNavigationButtons
            shouldAllowSpaceForHoverItems
            shouldStartOverflowAtMediumBreakpoint
            ctaAfterNavigationButtons={roleDetailsCta}
            css={styles.cardsCarousel(!!filters, itemsToShow.length === 1)}
          >
            {renderCarouselItems()}
          </OverflowCarousel>
        ) : (
          <div css={styles.cardsCarousel(!!filters, true)}>{renderCarouselItems()}</div>
        )}
      </div>

      {isLoading && (
        <div css={styles.navigationButtonsLoadingState}>
          <Skeleton variant="rect" height={25} width={300} />
        </div>
      )}
    </Grid>
  );
};

export default PathwaysCarouselCollections;
